import { useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import { getChainConfig, getPublicTokensForChain, getRpcUrlsForChain } from '@/chains';
import { fetchPodOracleTokenUsdPrice } from '@/vendor/coti-wallet-plugin';

const REFRESH_INTERVAL_MS = 60 * 1000;

/** Optional cache-wrapper views on the deployed PoDPriceOracle. */
const POD_ORACLE_META_ABI = [
    'function configuredOracle() view returns (address)',
    'function lastFetchTimestamp() view returns (uint256)',
    'function fetchInterval() view returns (uint256)',
];

/** Chainlink adapter (Fuji): token → aggregator proxy. */
const CHAINLINK_ADAPTER_ABI = [
    'function aggregators(address token) view returns (address aggregator)',
];

const CHAINLINK_AGGREGATOR_ABI = [
    'function latestRoundData() view returns (uint80 roundId, int256 answer, uint256 startedAt, uint256 updatedAt, uint80 answeredInRound)',
];

/** Band adapter (Sepolia): token → (base, quote) bytes32 pair. */
const BAND_ADAPTER_ABI = [
    'function feeds(address token) view returns (bytes32 base, bytes32 quote)',
];

const BAND_STD_REFERENCE_ABI = [
    'function getReferenceData(string base, string quote) view returns (uint256 rate, uint256 lastUpdatedBase, uint256 lastUpdatedQuote)',
];

export interface PodOracleTokenStatus {
    symbol: string;
    priceUsd: number | null;
    /** Feed last-update from Chainlink `updatedAt` or Band `lastUpdatedBase`. */
    lastUpdated: Date | null;
    /** True when getLivePrice returned 0 / null (unset, stale, or failed feed). */
    isStale: boolean;
    error: string | null;
}

export interface PodOracleStatus {
    oracleAddress: string;
    /** Underlying Band/Chainlink adapter when the oracle is a cache wrapper. */
    configuredOracleAddress: string | null;
    lastFetchAt: Date | null;
    fetchIntervalSeconds: number | null;
    tokens: PodOracleTokenStatus[];
    fetchedAt: Date;
}

export interface UsePodOracleStatusReturn {
    status: PodOracleStatus | null;
    isLoading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

function resolveTokenAddress(symbol: string, chainId: number): string | null {
    const config = getChainConfig(chainId);
    if (!config) return null;
    const token = config.tokens.find(t => t.symbol === symbol && !t.isPrivate);
    if (!token?.addressKey) return null;
    return config.addresses[token.addressKey] ?? null;
}

function bytes32ToSymbol(value: string): string {
    try {
        return ethers.decodeBytes32String(value).replace(/\0/g, '').trim();
    } catch {
        try {
            return ethers.toUtf8String(ethers.getBytes(value)).replace(/\0/g, '').trim();
        } catch {
            return '';
        }
    }
}

/** Band StdReference is typically stored at slot 1 on the Sepolia BandLiveOracle. */
async function resolveBandStdReference(
    adapterAddress: string,
    provider: ethers.JsonRpcProvider,
): Promise<string | null> {
    try {
        const slot = await provider.getStorage(adapterAddress, 1);
        const addr = ethers.getAddress(`0x${slot.slice(-40)}`);
        if (addr === ethers.ZeroAddress) return null;
        // Reject small ints (e.g. Fuji maxStaleness sitting in slot 1).
        if (BigInt(slot) < 2n ** 100n) return null;
        return addr;
    } catch {
        return null;
    }
}

/**
 * Reads the underlying feed's last-update timestamp for a token.
 * Fuji: Chainlink aggregator `latestRoundData().updatedAt`
 * Sepolia: Band `getReferenceData(base, quote).lastUpdatedBase`
 */
async function fetchFeedLastUpdated(
    adapterAddress: string,
    tokenAddress: string,
    provider: ethers.JsonRpcProvider,
    bandRefCache: { current: string | null | undefined },
): Promise<Date | null> {
    // Chainlink path
    try {
        const chainlinkAdapter = new ethers.Contract(adapterAddress, CHAINLINK_ADAPTER_ABI, provider);
        const aggregator: string = await chainlinkAdapter.aggregators(tokenAddress);
        if (aggregator && aggregator !== ethers.ZeroAddress) {
            const feed = new ethers.Contract(aggregator, CHAINLINK_AGGREGATOR_ABI, provider);
            const [, , , updatedAt] = (await feed.latestRoundData()) as [
                bigint,
                bigint,
                bigint,
                bigint,
                bigint,
            ];
            if (updatedAt > 0n) return new Date(Number(updatedAt) * 1000);
        }
    } catch {
        // Not a Chainlink adapter (or token has no aggregator) — try Band.
    }

    // Band path
    try {
        const bandAdapter = new ethers.Contract(adapterAddress, BAND_ADAPTER_ABI, provider);
        const [baseRaw, quoteRaw] = (await bandAdapter.feeds(tokenAddress)) as [string, string];
        const base = bytes32ToSymbol(baseRaw);
        const quote = bytes32ToSymbol(quoteRaw);
        if (!base || !quote) return null;

        if (bandRefCache.current === undefined) {
            bandRefCache.current = await resolveBandStdReference(adapterAddress, provider);
        }
        const bandRef = bandRefCache.current;
        if (!bandRef) return null;

        const ref = new ethers.Contract(bandRef, BAND_STD_REFERENCE_ABI, provider);
        const [, lastUpdatedBase] = (await ref.getReferenceData(base, quote)) as [
            bigint,
            bigint,
            bigint,
        ];
        if (lastUpdatedBase > 0n) return new Date(Number(lastUpdatedBase) * 1000);
    } catch {
        return null;
    }

    return null;
}

async function fetchOracleMeta(
    oracleAddress: string,
    provider: ethers.JsonRpcProvider,
): Promise<{
    configuredOracleAddress: string | null;
    lastFetchAt: Date | null;
    fetchIntervalSeconds: number | null;
}> {
    try {
        const oracle = new ethers.Contract(oracleAddress, POD_ORACLE_META_ABI, provider);
        const [configured, lastFetch, interval] = await Promise.all([
            oracle.configuredOracle() as Promise<string>,
            oracle.lastFetchTimestamp() as Promise<bigint>,
            oracle.fetchInterval() as Promise<bigint>,
        ]);
        return {
            configuredOracleAddress:
                configured && configured !== ethers.ZeroAddress ? configured : null,
            lastFetchAt: lastFetch > 0n ? new Date(Number(lastFetch) * 1000) : null,
            fetchIntervalSeconds: Number(interval),
        };
    } catch {
        return {
            configuredOracleAddress: null,
            lastFetchAt: null,
            fetchIntervalSeconds: null,
        };
    }
}

async function fetchStatus(chainId: number, oracleAddress: string): Promise<PodOracleStatus> {
    const rpcUrls = getRpcUrlsForChain(chainId);
    let lastError: unknown;

    for (const url of rpcUrls) {
        const provider = new ethers.JsonRpcProvider(url, undefined, { staticNetwork: true });
        try {
            const meta = await fetchOracleMeta(oracleAddress, provider);
            const publicTokens = getPublicTokensForChain(chainId);
            const bandRefCache: { current: string | null | undefined } = { current: undefined };

            const tokens: PodOracleTokenStatus[] = await Promise.all(
                publicTokens.map(async (token) => {
                    try {
                        const priceUsd = await fetchPodOracleTokenUsdPrice(
                            token.symbol,
                            chainId,
                            provider,
                        );
                        const hasLive = priceUsd != null && priceUsd > 0;
                        const tokenAddress = resolveTokenAddress(token.symbol, chainId);
                        let lastUpdated: Date | null = null;
                        if (meta.configuredOracleAddress && tokenAddress) {
                            lastUpdated = await fetchFeedLastUpdated(
                                meta.configuredOracleAddress,
                                tokenAddress,
                                provider,
                                bandRefCache,
                            );
                        }
                        return {
                            symbol: token.symbol,
                            priceUsd: hasLive ? priceUsd : null,
                            lastUpdated,
                            isStale: !hasLive,
                            error: hasLive ? null : 'no live feed',
                        } satisfies PodOracleTokenStatus;
                    } catch (err: unknown) {
                        const message = err instanceof Error ? err.message : 'feed unavailable';
                        return {
                            symbol: token.symbol,
                            priceUsd: null,
                            lastUpdated: null,
                            isStale: true,
                            error: message,
                        } satisfies PodOracleTokenStatus;
                    }
                }),
            );

            return {
                oracleAddress,
                configuredOracleAddress: meta.configuredOracleAddress,
                lastFetchAt: meta.lastFetchAt,
                fetchIntervalSeconds: meta.fetchIntervalSeconds,
                tokens,
                fetchedAt: new Date(),
            };
        } catch (err) {
            lastError = err;
        } finally {
            provider.destroy();
        }
    }

    throw lastError instanceof Error ? lastError : new Error('Failed to reach the PoD oracle');
}

/**
 * Per-token PoDPriceOracle feed status for Sepolia / Fuji portals.
 * Pass null to disable. Auto-refreshes every minute.
 */
export const usePodOracleStatus = (chainId: number | null): UsePodOracleStatusReturn => {
    const oracleAddress =
        chainId != null ? (getChainConfig(chainId)?.priceOracleAddress ?? null) : null;
    const [status, setStatus] = useState<PodOracleStatus | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(oracleAddress != null);
    const [error, setError] = useState<string | null>(null);
    /** Bumped on each fetch so a slow prior-chain response cannot overwrite the new chain. */
    const requestIdRef = useRef(0);

    const refresh = useCallback(async () => {
        if (chainId == null || !oracleAddress) {
            requestIdRef.current += 1;
            setStatus(null);
            setIsLoading(false);
            setError(null);
            return;
        }
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        try {
            const next = await fetchStatus(chainId, oracleAddress);
            if (requestId !== requestIdRef.current) return;
            setStatus(next);
            setError(null);
        } catch (err: unknown) {
            if (requestId !== requestIdRef.current) return;
            setError(err instanceof Error ? err.message : 'Failed to fetch PoD oracle status');
            setStatus(null);
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [chainId, oracleAddress]);

    useEffect(() => {
        // Drop previous chain's rows immediately so Fuji/Sepolia don't briefly share a table.
        setStatus(null);
        setError(null);
        setIsLoading(oracleAddress != null);
        void refresh();
        const interval = setInterval(() => {
            void refresh();
        }, REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [refresh, oracleAddress]);

    return { status, isLoading, error, refresh };
};
